import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readLocalJSON, writeLocalJSON } from '../lib/storage';
import { log } from '../lib/logger';
import SafetyAckModal, { hasSafetyAck } from '../components/SafetyAckModal';

const CartContext = createContext();
const STORAGE_KEY = 'peptihub_cart';

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

// --- Pure helpers (module-level — keep hook bodies trivial) ---

const upsertItem = (items, product, qty) => {
  const existing = items.find((i) => i.id === product.id);
  if (existing) return items.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
  return [...items, { ...product, qty }];
};

const computeTotals = (items) => {
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const uniqueItems = items.length;
  const totalItems = items.reduce((s, i) => s + i.qty, 0);
  const bundleDiscount = uniqueItems >= 3 ? subtotal * 0.15 : 0;
  return { subtotal, totalItems, uniqueItems, bundleDiscount, total: subtotal - bundleDiscount };
};

const persistCart = (items) => {
  const ok = writeLocalJSON(STORAGE_KEY, items);
  if (!ok) log.warn('Cart persist skipped (private mode / quota)');
};

const addItemAction = (setItems, setIsOpen, product, qty) => {
  setItems((prev) => upsertItem(prev, product, qty));
  setIsOpen(true);
};

const removeItemAction = (setItems, id) => {
  setItems((prev) => prev.filter((i) => i.id !== id));
};

const updateQtyAction = (setItems, id, qty) => {
  if (qty <= 0) {
    setItems((prev) => prev.filter((i) => i.id !== id));
    return;
  }
  setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => readLocalJSON(STORAGE_KEY, []));
  const [isOpen, setIsOpen] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const pendingAddRef = useRef(null);

  useEffect(() => {
    persistCart(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, persistCart]);

  // Gated addItem — first call in a session opens the safety modal; the actual
  // add is queued and replayed on acceptance. Once accepted, subsequent adds
  // pass through immediately. If the user re-clicks "Add" while the modal is
  // open (e.g. tapping twice to get qty=2), accumulate the queued qty for the
  // same product so they don't silently lose the second click.
  const addItem = useCallback((product, qty = 1) => {
    if (hasSafetyAck()) {
      addItemAction(setItems, setIsOpen, product, qty);
      return;
    }
    const existing = pendingAddRef.current;
    if (existing && existing.product?.id === product.id) {
      pendingAddRef.current = { product, qty: existing.qty + qty };
    } else {
      pendingAddRef.current = { product, qty };
    }
    setAckOpen(true);
  }, [setItems, setIsOpen]);

  const onAckAccept = useCallback(() => {
    setAckOpen(false);
    const pending = pendingAddRef.current;
    pendingAddRef.current = null;
    if (pending) addItemAction(setItems, setIsOpen, pending.product, pending.qty);
  }, [setItems, setIsOpen]);

  const onAckCancel = useCallback(() => {
    pendingAddRef.current = null;
    setAckOpen(false);
  }, []);

  const removeItem = useCallback(
    (id) => removeItemAction(setItems, id),
    [setItems, removeItemAction], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const updateQty = useCallback(
    (id, qty) => updateQtyAction(setItems, id, qty),
    [setItems, updateQtyAction], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const clear = useCallback(() => setItems([]), [setItems]);

  const totals = useMemo(() => computeTotals(items), [items]);
  const actions = useMemo(() => ({ addItem, removeItem, updateQty, clear }), [addItem, removeItem, updateQty, clear]);

  const value = useMemo(
    () => ({ items, isOpen, setIsOpen, ...actions, ...totals }),
    [items, isOpen, setIsOpen, actions, totals],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      <SafetyAckModal open={ackOpen} onAccept={onAckAccept} onCancel={onAckCancel} />
    </CartContext.Provider>
  );
};
